import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../../assets/logo-navbar.png';
import Myriade from "../../assets/Myriade Logo Blanc.png";
const PremiumNavbar = () => {

  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
    setActiveDropdown(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  const handleNavigation = (path) => {
    setIsOpen(false);
    setActiveDropdown(null);
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleHomeNavigation = () => {
    if (location.pathname === '/') {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      handleNavigation('/');
    }
  };

  const navItems = [
    { path: "/", label: "Accueil", linear: "from-[#32b34a] to-[#287bb3]" },
    { path: "/Services", label: "Services", linear: "from-[#feed08] to-[#32b34a]" },
    { path: "/Projets", label: "Projets Réalisés", linear: "from-[#32b34a] to-[#287bb3]" },
    { path: "/Technologies", label: "Technologies", linear: "from-[#5d19a8] to-[#feed08]" },
    { path: "/Propos", label: "À Propos", linear: "from-[#287bb3] to-[#24295c]" },
    // { path: "/Temoignages", label: "Témoignages", linear: "from-[#24295c] to-[#5d19a8]" },
    { path: "/Contact", label: "Contact", linear: "from-[#5d19a8] to-[#feed08]" },
    // { path: "/PageErreur", label: "PageErreur", linear: "from-[#5d19a8] to-[#feed08]" },

  ];

  const itemVariants = {
    hidden: { opacity: 0, y: -10 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: { delay: i * 0.1, duration: 0.5, ease: "easeOut" }
    })
  };

  const mobileItemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (i) => ({
      opacity: 1,
      x: 0,
      transition: { delay: i * 0.1, duration: 0.3, ease: "easeOut" }
    })
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setIsOpen(false)}
            className="fixed inset-0 bg-black/40 backdrop-blur-lg z-40 lg:hidden"
          />
        )}
      </AnimatePresence>

      <motion.nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${scrolled
          ? 'bg-black/20 backdrop-blur-xl border-b border-white/5 shadow-2xl'
          : 'bg-transparent'
          }`}
        style={{ backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)' }}
      >
        <div className="max-w-8xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center">

            <motion.div
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="flex items-center cursor-pointer"
              onClick={handleHomeNavigation}
            >
              <img
                src={Myriade}
                alt="Myriade"
                className="relative w-30 py-5 h-auto z-10 transition-all duration-300 hover:drop-shadow-lg"
              />

            </motion.div>

            <div className="hidden lg:flex items-center space-x-1">
              {navItems.map((item, index) => {
                const isActive = location.pathname === item.path;
                return (
                  <motion.div
                    key={item.path}
                    custom={index}
                    variants={itemVariants}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="relative"
                  >
                    <button
                      onClick={() => handleNavigation(item.path)}
                      className="relative px-4 py-2 text-gray-800 font-medium transition-all duration-300 group cursor-pointer"
                    >
                      <motion.div
                        className={`absolute inset-0 rounded-full bg-linear-to-r ${item.linear} opacity-0 group-hover:opacity-20 transition-opacity duration-300`}
                      />

                      {isActive && (
                        <motion.div
                          layoutId="active-indicator"
                          className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-3/4 h-1 bg-linear-to-r from-[#32b34a] to-[#287bb3] rounded-full"
                          initial={false}
                          transition={{ type: "spring", stiffness: 300, damping: 30 }}
                        />
                      )}

                      {/* 🔥 Taille réduite ici */}
                      <span
                        className={`relative z-10 transition-colors text-[15px] font-medium ${isActive
                          ? 'text-white font-bold'
                          : 'text-white/90 group-hover:text-white'
                          }`}
                      >
                        {item.label}
                      </span>

                      <motion.div
                        className="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-linear-to-r from-transparent via-[#287bb3] to-transparent"
                        initial={{ scaleX: 0 }}
                        whileHover={{ scaleX: 1 }}
                        transition={{ duration: 0.3 }}
                      />
                    </button>
                  </motion.div>
                );
              })}
            </div>

            <motion.button
              whileHover={{ scale: 1.1 }}
              whileTap={{ scale: 0.9 }}
              onClick={() => setIsOpen(!isOpen)}
              className="lg:hidden p-2.5 rounded-xl bg-linear-to-r from-white to-gray-50 shadow-lg border border-gray-200 cursor-pointer"
            >
              {isOpen ? (
                <X className="w-7 h-7 text-[#24295c]" />
              ) : (
                <Menu className="w-6 h-6 text-[#24295c]" />
              )}
            </motion.button>

          </div>
        </div>

        <AnimatePresence>
          {isOpen && (
            <motion.div
              key="mobile-menu"
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.3, ease: "easeInOut" }}
              className="lg:hidden absolute top-full left-4 right-4 mt-2 bg-white/98 backdrop-blur-xl rounded-2xl shadow-2xl overflow-hidden border border-gray-200/50"
            >
              <div className="p-4 space-y-1">
                {navItems.map((item, index) => {
                  const isActive = location.pathname === item.path;
                  return (
                    <motion.button
                      key={item.path}
                      custom={index}
                      variants={mobileItemVariants}
                      initial="hidden"
                      animate="visible"
                      exit="hidden"
                      onClick={() => handleNavigation(item.path)}
                      className={`w-full text-left px-6 py-4 cursor-pointer rounded-xl transition-all duration-300 flex items-center hover:scale-102 justify-between ${isActive
                        ? 'bg-linear-to-r from-[#32b34a]/10 to-[#287bb3]/10 text-[#24295c] font-bold'
                        : 'hover:bg-gray-50/80 text-gray-700'
                        }`}
                    >
                      {/* 🔥 Taille mobile réduite aussi */}
                      <span className="text-[16px] font-medium">{item.label}</span>

                      {isActive && (
                        <motion.div
                          layoutId="mobile-active-indicator"
                          className="w-3 h-3 bg-linear-to-r from-[#32b34a] to-[#287bb3] rounded-full"
                        />
                      )}
                    </motion.button>
                  );
                })}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

      </motion.nav>

    </>
  );
};

export default PremiumNavbar;
